import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Button, Icon, Text, Skeleton } from '../../../../components';
import { CASINO_SETTINGS, COLORS, DELTA_PILL } from '../../../../theme/tokens';
import type { CasinoDomain, CasinoDomainStatus } from '../../../../mock-data/types';
import {
  List,
  Card,
  Head,
  HeadLeft,
  DomainName,
  PrimaryTag,
  StatusTag,
  TypeRow,
  TypeTag,
  Meta,
  MetaItem,
  MetaLabel,
  MetaValue,
  Divider,
  Footer,
  ManagedNote,
  ActionNote,
  VisitLink,
  SkelStack,
  UnknownTag,
  UnknownNote,
} from './DomainsSection.styles';

export type DomainsSectionProps = {
  domains: CasinoDomain[];
  loading: boolean;
  /** The casino's live web address from the API (`casino_data.details.domain`), shown on
   *  the main domain card and opened by its "Visit site" link. Undefined for brands that
   *  have no domain on record yet — then the card falls back to its listed name. */
  liveDomain?: string;
  /** Open the DNS-records overlay for an unmanaged domain. */
  onViewDns: (domain: CasinoDomain) => void;
};

/** Outbound-link glyph — an open box with an arrow leaving the top-right corner; same
 *  marker used on the running-campaign Top-Websites rows. Flags the "Visit site" link as
 *  opening the live casino in a new tab. */
const ExternalLinkIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 5h5v5" />
    <path d="M19 5l-7 7" />
    <path d="M18 13.5v4A1.5 1.5 0 0 1 16.5 19h-10A1.5 1.5 0 0 1 5 17.5v-10A1.5 1.5 0 0 1 6.5 6h4" />
  </svg>
);

/** The API stores a bare host (e.g. "luckyspin.com"); make it an absolute https URL so the
 *  browser opens the live site rather than treating it as a relative path. */
const toHref = (domain: string): string => (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);

/** Status pill tint, keyed by the domain's connection state. The English label is the
 *  i18n key, translated reactively at the render site (see `statusLabel`). */
const STATUS: Record<CasinoDomainStatus, { label: string; bg: string; fg: string }> = {
  active: { label: 'Active', bg: CASINO_SETTINGS.okBg, fg: CASINO_SETTINGS.okFg },
  pending: { label: 'Pending', bg: CASINO_SETTINGS.lockBg, fg: CASINO_SETTINGS.lockFg },
  'action-required': { label: 'Action required', bg: DELTA_PILL.negative.bg, fg: DELTA_PILL.negative.fg },
};

/**
 * Domains — the web addresses the casino is reachable on. Managed domains were
 * bought through the deploy process and need nothing from the owner; unmanaged
 * domains were connected via Entri and expose the DNS records the owner must keep
 * set at their registrar (opened in the DNS overlay, same as Settings → Website
 * Domain). Gates its reveal on `loading`.
 */
export const DomainsSection = ({
  domains,
  loading,
  liveDomain,
  onViewDns,
}: DomainsSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  // Reactive translations for the closed set of status labels (the constant keeps the
  // English keys + tints; these literals are what the catalog harvests).
  const statusLabel: Record<CasinoDomainStatus, string> = {
    active: t('Active'),
    pending: t('Pending'),
    'action-required': t('Action required'),
  };
  return (
    <Panel>
      <SectionTitle title={t('Website Domain')} icon="globe" />

      {loading ? (
        <SkelStack>
          <Skeleton block height={132} radius={14} />
          <Skeleton block height={132} radius={14} />
        </SkelStack>
      ) : domains.length === 0 ? (
        // No real domain record for this brand (the API returned DOMAIN_NOT_FOUND or
        // an unparseable payload). Never fabricate a domain: show an honest "unknown"
        // card. The live address itself IS real (off the brand record), so surface it
        // and its "Visit site" link when present; everything else reads as unknown.
        <List>
          <Card>
            <Head>
              <HeadLeft>
                <Icon name="globe" size={18} color={COLORS.iconBright} />
                <DomainName text={liveDomain ?? t('Website domain')} tag="span" title={liveDomain} />
              </HeadLeft>
              <UnknownTag>
                <Icon name="info" size={12} color={COLORS.textMuted} />
                <Text text={t('Unknown')} tag="span" />
              </UnknownTag>
            </Head>
            <UnknownNote text="We don't have full domain details for this casino." tag="span" />
            {liveDomain ? (
              <>
                <Divider />
                <Footer>
                  <VisitLink
                    href={toHref(liveDomain)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('Open {{name}} in a new tab', { name: liveDomain })}
                  >
                    <Text text="Visit site" tag="span" />
                    <ExternalLinkIcon color={COLORS.ctaSolidText} />
                  </VisitLink>
                </Footer>
              </>
            ) : null}
          </Card>
        </List>
      ) : (
        <List>
          {domains.map((domain) => {
            const status = STATUS[domain.status];
            const managed = domain.type === 'managed';
            // The casino's live address comes from the API; pin it to the main card (the
            // primary domain, or the only one) — that's the entry the API value describes.
            const isLive = (domain.primary || domains.length === 1) && Boolean(liveDomain);
            const name = isLive && liveDomain ? liveDomain : domain.name;
            return (
              <Card key={domain.id}>
                <Head>
                  <HeadLeft>
                    <Icon name="globe" size={18} color={COLORS.iconBright} />
                    <DomainName text={name} tag="span" title={name} />
                    {domain.primary && domains.length > 1 ? <PrimaryTag text="Primary" tag="span" /> : null}
                  </HeadLeft>
                  {domain.status !== 'pending' ? (
                    <StatusTag $bg={status.bg} $fg={status.fg}>
                      <Icon name={domain.status === 'active' ? 'check' : 'warning'} size={12} color={status.fg} />
                      <Text text={statusLabel[domain.status]} tag="span" />
                    </StatusTag>
                  ) : null}
                </Head>

                <TypeRow>
                  <TypeTag $fg={managed ? CASINO_SETTINGS.okFg : CASINO_SETTINGS.cardIconSelected}>
                    <Icon
                      name={managed ? 'check' : 'globe'}
                      size={13}
                      color={managed ? CASINO_SETTINGS.okFg : CASINO_SETTINGS.cardIconSelected}
                    />
                    {managed ? (
                      <Text text="Managed by Whitelabels" tag="span" />
                    ) : (
                      <Text text="Connected via Entri" tag="span" />
                    )}
                  </TypeTag>
                </TypeRow>

                {managed ? null : (
                  <Meta>
                    <MetaItem>
                      <MetaLabel text="Registrar" tag="span" />
                      <MetaValue text={domain.registrar} tag="span" title={domain.registrar} />
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel text="Connected" tag="span" />
                      <MetaValue text={domain.connectedOn ?? '–'} tag="span" />
                    </MetaItem>
                  </Meta>
                )}

                <Divider />

                <Footer>
                  {managed ? (
                    <ManagedNote text="DNS is configured automatically, nothing to do here." tag="span" />
                  ) : (
                    <>
                      <Button label={t('View DNS settings')} variant="outline" onClick={() => onViewDns(domain)} />
                      {domain.status === 'action-required' ? (
                        <ActionNote text="Add the DNS records to finish connecting this domain." tag="span" />
                      ) : null}
                    </>
                  )}
                  {isLive && liveDomain ? (
                    <VisitLink
                      href={toHref(liveDomain)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('Open {{name}} in a new tab', { name: liveDomain })}
                    >
                      <Text text="Visit site" tag="span" />
                      <ExternalLinkIcon color={COLORS.ctaSolidText} />
                    </VisitLink>
                  ) : null}
                </Footer>
              </Card>
            );
          })}
        </List>
      )}
    </Panel>
  );
};

export default DomainsSection;
